/**
 * GridCrash — Share Grid
 *
 * Compresses a finished board into a Wordle-style emoji grid (max 5×5)
 * for the clipboard/native share payload. Pure functions, no DOM.
 *
 * Each output cell summarizes a block of the real board. Block glyphs,
 * by priority:
 *   💥 the fatal mine (LOSE only)
 *   🟢 the opening reveal
 *   🚩 at least one correctly flagged mine
 *   🟩 mostly cleared (≥60% of safe cells revealed)
 *   🟨 partially cleared (≥20%)
 *   ⬛ untouched
 */

import type { Cell } from "./engine.js";

export interface ShareGridInput {
  rows: number;
  cols: number;
  grid: ReadonlyArray<ReadonlyArray<Cell>>;
  /** [row, col] of the first reveal, or null if unknown. */
  startCell: [number, number] | null;
  /** [row, col] of the mine that ended a loss, or null on a win. */
  fatalCell: [number, number] | null;
}

const MAX_DIM = 5;

/** Returns the emoji grid as newline-joined rows (≤5 rows of ≤5 glyphs). */
export function buildEmojiGrid(input: ShareGridInput): string {
  const { rows, cols, grid, startCell, fatalCell } = input;
  const outRows = Math.min(rows, MAX_DIM);
  const outCols = Math.min(cols, MAX_DIM);

  const lines: string[] = [];
  for (let br = 0; br < outRows; br++) {
    const r0 = Math.floor((br * rows) / outRows);
    const r1 = Math.floor(((br + 1) * rows) / outRows);
    let line = "";
    for (let bc = 0; bc < outCols; bc++) {
      const c0 = Math.floor((bc * cols) / outCols);
      const c1 = Math.floor(((bc + 1) * cols) / outCols);
      line += blockGlyph(grid, r0, r1, c0, c1, startCell, fatalCell);
    }
    lines.push(line);
  }
  return lines.join("\n");
}

function blockGlyph(
  grid: ReadonlyArray<ReadonlyArray<Cell>>,
  r0: number, r1: number,
  c0: number, c1: number,
  startCell: [number, number] | null,
  fatalCell: [number, number] | null
): string {
  const inBlock = (p: [number, number] | null): boolean =>
    p !== null && p[0] >= r0 && p[0] < r1 && p[1] >= c0 && p[1] < c1;

  if (inBlock(fatalCell)) return "💥";
  if (inBlock(startCell)) return "🟢";

  let safeCells = 0;
  let revealedSafe = 0;
  let correctFlags = 0;

  for (let r = r0; r < r1; r++) {
    for (let c = c0; c < c1; c++) {
      const cell = grid[r][c];
      if (cell.isMine) {
        if (cell.isFlagged) correctFlags++;
      } else {
        safeCells++;
        if (cell.isRevealed) revealedSafe++;
      }
    }
  }

  if (correctFlags > 0) return "🚩";
  const ratio = safeCells > 0 ? revealedSafe / safeCells : 0;
  if (ratio >= 0.6) return "🟩";
  if (ratio >= 0.2) return "🟨";
  return "⬛";
}

/**
 * Full share payload: header line, emoji grid, site URL.
 * Example:
 *   GridClash — Expert ⏱️ 02:41 | Score: 1,420 pts | 🚩 99/99
 *   🟢⬛🟨🚩⬛
 *   ...
 *   https://gridcrashbeta.netlify.app
 */
export function buildShareText(opts: {
  won: boolean;
  score: number;
  diffLabel: string;
  timeLabel: string;
  correctFlags: number;
  mineCount: number;
  emojiGrid: string;
}): string {
  const { won, score, diffLabel, timeLabel, correctFlags, mineCount, emojiGrid } = opts;
  const outcome = won ? "🏁" : "💥";
  const header =
    `GridClash ${outcome} ${diffLabel} ⏱️ ${timeLabel} | ` +
    `Score: ${score.toLocaleString()} pts | 🚩 ${correctFlags}/${mineCount}`;
  return `${header}\n${emojiGrid}\nhttps://gridcrashbeta.netlify.app`;
}
